import { useState, useEffect, useRef, useCallback, ReactNode } from 'react'

export interface SelectOption {
  value: string
  label: string
  sublabel?: string
  group?: string // For grouping options
  disabled?: boolean
}

interface SearchableSelectProps {
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  error?: string
  searchable?: boolean
  icon?: string
  required?: boolean
  className?: string
  helperText?: string
  minWidth?: string // Minimum width for the dropdown (e.g., '200px', '14rem')
  dropdownWidth?: string // Width for dropdown if different from trigger
  maxVisible?: number // Show only this many options initially, with "Show more" button
  renderOption?: (option: SelectOption, isSelected: boolean, isHighlighted: boolean) => ReactNode
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  error,
  searchable = true,
  icon,
  className,
  helperText,
  minWidth,
  dropdownWidth,
  maxVisible,
  renderOption,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [showAll, setShowAll] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(o => o.value === value)

  const isSearching = search.length > 0

  const allFiltered = options.filter(o => {
    if (o.disabled) return false
    if (!isSearching) return true
    const s = search.toLowerCase()
    return o.label.toLowerCase().includes(s) || 
           (o.sublabel && o.sublabel.toLowerCase().includes(s)) ||
           (o.group && o.group.toLowerCase().includes(s))
  })

  // Apply maxVisible: when not searching and not expanded, truncate the list
  const shouldTruncate = maxVisible && !isSearching && !showAll && allFiltered.length > maxVisible
  const filtered = shouldTruncate ? allFiltered.slice(0, maxVisible) : allFiltered
  const hiddenCount = shouldTruncate ? allFiltered.length - maxVisible : 0

  // Group options by their group property
  const groupedOptions = filtered.reduce((acc, opt) => {
    const group = opt.group || ''
    if (!acc[group]) acc[group] = []
    acc[group].push(opt)
    return acc
  }, {} as Record<string, SelectOption[]>)

  const hasGroups = Object.keys(groupedOptions).some(g => g !== '')

  // Click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
        setShowAll(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Scroll highlighted into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement
      if (el) el.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  const handleSelect = useCallback((opt: SelectOption) => {
    onChange(opt.value)
    setIsOpen(false)
    setSearch('')
    setHighlightedIndex(-1)
    setShowAll(false)
  }, [onChange])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      setIsOpen(true)
      return
    }
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => prev < filtered.length - 1 ? prev + 1 : 0)
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : filtered.length - 1)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
          handleSelect(filtered[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearch('')
        setHighlightedIndex(-1)
        break
    }
  }, [isOpen, highlightedIndex, filtered, handleSelect])

  const handleOpen = () => {
    setIsOpen(true)
    setHighlightedIndex(-1)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const containerStyle = minWidth ? { minWidth } : {}

  return (
    <div ref={containerRef} className={`relative ${className || ''}`} style={containerStyle}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        className={`w-full flex items-center justify-between px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
          error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon && <i className={`fas ${icon} text-gray-400 text-sm`}></i>}
          {selectedOption ? (
            <span className="text-gray-900 dark:text-white truncate">{selectedOption.label}</span>
          ) : (
            <span className="text-gray-400 dark:text-gray-500 truncate">{placeholder}</span>
          )}
        </div>
        <i className={`fas fa-chevron-down text-gray-400 text-xs transition-transform ml-2 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden"
          style={{ width: dropdownWidth || '100%', minWidth: minWidth || 'auto' }}
        >
          {/* Search input */}
          {searchable && (
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setHighlightedIndex(-1)
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Type to search..."
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          {/* Options list */}
          <div ref={listRef} className="max-h-72 overflow-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No options found
              </div>
            ) : hasGroups ? (
              // Render with groups
              Object.entries(groupedOptions).map(([group, groupOpts]) => (
                <div key={group || 'ungrouped'}>
                  {group && (
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                      {group}
                    </div>
                  )}
                  {groupOpts.map((opt) => {
                    const index = filtered.indexOf(opt)
                    return (
                      <div
                        key={opt.value}
                        onClick={() => !opt.disabled && handleSelect(opt)}
                        onMouseEnter={() => !opt.disabled && setHighlightedIndex(index)}
                      >
                        {renderOption ? (
                          renderOption(opt, opt.value === value, highlightedIndex === index)
                        ) : (
                          <div className={`flex items-center justify-between px-4 py-2.5 transition-colors ${
                            opt.disabled 
                              ? 'opacity-50 cursor-not-allowed'
                              : highlightedIndex === index
                                ? 'bg-primary-50 dark:bg-primary-900/30 cursor-pointer'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
                          }`}>
                            <div className="min-w-0">
                              <p className={`text-sm ${
                                opt.value === value
                                  ? 'font-semibold text-primary-600 dark:text-primary-400'
                                  : 'text-gray-900 dark:text-white'
                              }`}>
                                {opt.label}
                              </p>
                              {opt.sublabel && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{opt.sublabel}</p>
                              )}
                            </div>
                            {opt.value === value && (
                              <i className="fas fa-check text-primary-600 dark:text-primary-400 text-xs ml-2 flex-shrink-0"></i>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))
            ) : (
              // Render without groups
              filtered.map((opt, index) => (
                <div
                  key={opt.value}
                  onClick={() => !opt.disabled && handleSelect(opt)}
                  onMouseEnter={() => !opt.disabled && setHighlightedIndex(index)}
                >
                  {renderOption ? (
                    renderOption(opt, opt.value === value, highlightedIndex === index)
                  ) : (
                    <div className={`flex items-center justify-between px-4 py-2.5 transition-colors ${
                      opt.disabled 
                        ? 'opacity-50 cursor-not-allowed'
                        : highlightedIndex === index
                          ? 'bg-primary-50 dark:bg-primary-900/30 cursor-pointer'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
                    }`}>
                      <div className="min-w-0">
                        <p className={`text-sm ${
                          opt.value === value
                            ? 'font-semibold text-primary-600 dark:text-primary-400'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {opt.label}
                        </p>
                        {opt.sublabel && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{opt.sublabel}</p>
                        )}
                      </div>
                      {opt.value === value && (
                        <i className="fas fa-check text-primary-600 dark:text-primary-400 text-xs ml-2 flex-shrink-0"></i>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
            {/* Show more / Show less button */}
            {maxVisible && !isSearching && allFiltered.length > maxVisible && (
              <div
                onClick={() => setShowAll(prev => !prev)}
                className="px-4 py-2 text-xs font-medium text-primary-600 dark:text-primary-400 cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 text-center border-t border-gray-100 dark:border-gray-700"
              >
                {showAll ? (
                  <span><i className="fas fa-chevron-up mr-1"></i>Show less</span>
                ) : (
                  <span><i className="fas fa-chevron-down mr-1"></i>Show {hiddenCount} more older months</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {helperText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helperText}</p>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
          <i className="fas fa-exclamation-circle text-xs"></i>
          {error}
        </p>
      )}
    </div>
  )
}
